import { toValue, type MaybeRefOrGetter } from 'vue'
import { useQuery, useMutation, useQueryCache } from '@pinia/colada'
import { useMyWorkService as useMyWorkServiceBase } from './my-work.composable'
import { CACHE_TTL, CACHE_KEYS } from '@/services/cache'
import { useAuthStore } from '@/stores/auth.store'
import { readLastSeenMyWork, rememberMyWork } from './my-work.last-seen'
import { withPrDetails, withRefreshedPr } from './my-work-refresh.utils'
import type {
  AutoMergeRequest,
  AutoMergeResponse,
  ActionChoices,
  ActionOffer,
  AnswerDecisionRequest,
  AnswerDecisionResult,
  MergePreviewResponse,
  MergePrsResponse,
  MergeSettings,
  MyWork,
  MyWorkCounts,
  MyPr,
  PrDetailsResponse,
  PrRefreshResponse,
  SaveMergeSettingsRequest,
  StartActionResult
} from './my-work.types'
import type { StartSweepResult } from './my-work-sweep.types'

export interface StartActionVariables {
  offer: ActionOffer
  target: string
  choices?: ActionChoices
}

/**
 * My work service with caching and mutation invalidation.
 *
 * Architecture: View → my-work.cached.ts → my-work.composable.ts → my-work.api.ts
 *
 * The action list's polling is not here: GET /me/actions is a live progress check, read by the
 * my-work store while a request is still queued or running.
 */
export function useMyWorkService() {
  const queryCache = useQueryCache()
  const service = useMyWorkServiceBase()

  const invalidateMyWork = () => queryCache.invalidateQueries({ key: CACHE_KEYS.myWork.all() })

  /**
   * The side nav badge reads the fleet's cached page, which a fresh read of the page has just renewed. A failed
   * badge read stays on the badge's own query, which shows no badge; it must not fail the page that asked for it.
   */
  const invalidateCounts = () => {
    queryCache.invalidateQueries({ key: CACHE_KEYS.myWork.counts(), exact: true }).catch(() => undefined)
  }

  /**
   * The person's tickets, PRs, decisions and recent actions: one read per visit, made only when the
   * cached answer is older than CACHE_TTL.MY_WORK. A reload shows the last answer this browser saw
   * while the read runs. `reload` joins a read already in flight rather than starting another.
   */
  const useMyWork = (options: { enabled?: MaybeRefOrGetter<boolean> } = {}) => {
    const authStore = useAuthStore()
    const query = useQuery({
      key: CACHE_KEYS.myWork.overview(),
      query: async (): Promise<MyWork> => {
        const work = await service.getMyWork()
        rememberMyWork(work)
        invalidateCounts()
        return work
      },
      staleTime: CACHE_TTL.MY_WORK,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: () => readLastSeenMyWork(authStore.user?.email),
      enabled: () => (options.enabled === undefined ? true : toValue(options.enabled))
    })
    const reload = (): Promise<unknown> =>
      queryCache.get<MyWork>(CACHE_KEYS.myWork.overview())?.pending?.refreshCall ?? query.refetch()
    return { ...query, reload }
  }

  /** This engineer's laptop runner tally; a failed read is left quiet, since the line is only a courtesy. */
  const useMyMachineThanks = (email: MaybeRefOrGetter<string>) => {
    return useQuery({
      key: () => [...CACHE_KEYS.myWork.all(), 'machine', toValue(email)],
      query: () => service.getMyMachineThanks(toValue(email)),
      staleTime: CACHE_TTL.MY_WORK,
      enabled: () => Boolean(toValue(email))
    })
  }

  /**
   * Reads past the fleet's short cache and puts the answer where `useMyWork` reads it. A plain read
   * still in flight is dropped, so its older answer cannot land on top of this one.
   */
  const useRefreshMyWork = () => {
    return useMutation({
      mutation: (): Promise<MyWork> => {
        queryCache.cancelQueries({ key: CACHE_KEYS.myWork.overview(), exact: true })
        return service.getMyWork({ refresh: true })
      },
      onSuccess: work => {
        rememberMyWork(work)
        queryCache.setQueryData(CACHE_KEYS.myWork.overview(), work)
        invalidateCounts()
      }
    })
  }

  /** Starts an action on a ticket or PR; the fleet answers with the queued request. */
  const useStartAction = () => {
    return useMutation({
      mutation: (vars: StartActionVariables): Promise<StartActionResult> =>
        service.startAction(vars.offer, vars.target, vars.choices),
      onSuccess: invalidateMyWork
    })
  }

  /** Answers a "Decision needed" question; the fleet posts it on the ticket as a relayed pick. */
  const useAnswerDecision = () => {
    return useMutation({
      mutation: (answer: AnswerDecisionRequest): Promise<AnswerDecisionResult> =>
        service.answerDecision(answer),
      onSuccess: invalidateMyWork
    })
  }

  /** Queues the person's own sweep on a fleet runner; the fleet answers with it, queued. */
  const useStartSweep = () => {
    return useMutation({
      mutation: (): Promise<StartSweepResult> => service.startSweep(),
      onSuccess: invalidateMyWork
    })
  }

  /**
   * The side nav badge's counts. They come from the fleet's own cached page, so a read is cheap; the
   * badge polls through `refetch`, and any My work action invalidates them with the page.
   */
  const useMyWorkCounts = (options: { enabled?: MaybeRefOrGetter<boolean> } = {}) => {
    return useQuery({
      key: CACHE_KEYS.myWork.counts(),
      query: (): Promise<MyWorkCounts> => service.getCounts(),
      staleTime: CACHE_TTL.MY_WORK,
      refetchOnWindowFocus: true,
      enabled: () => (options.enabled === undefined ? true : toValue(options.enabled))
    })
  }

  const useMergePrs = () => {
    return useMutation({
      mutation: (targets: string[]): Promise<MergePrsResponse> => service.mergePrs(targets),
      onSuccess: invalidateMyWork
    })
  }

  const useSetAutoMerge = () => {
    return useMutation({
      mutation: (vars: AutoMergeRequest): Promise<AutoMergeResponse> => service.setAutoMerge(vars),
      onSuccess: invalidateMyWork
    })
  }

  /**
   * Reads one PR again and puts it in place on the page, leaving every other row as it was: a whole
   * page read costs far more of the team's shared Bitbucket budget.
   */
  const useRefreshPr = () => {
    return useMutation({
      mutation: (pr: Pick<MyPr, 'repo' | 'id'>): Promise<PrRefreshResponse> =>
        service.refreshPr(pr.repo, pr.id),
      onSuccess: refreshed => {
        const work = queryCache.getQueryData<MyWork>(CACHE_KEYS.myWork.overview())
        if (!work) return
        const updated = withRefreshedPr(work, refreshed)
        rememberMyWork(updated)
        queryCache.setQueryData(CACHE_KEYS.myWork.overview(), updated)
        invalidateCounts()
      }
    })
  }

  /** A folded group's rows read when the person opens it, each put back on the page in place. */
  const usePrDetails = () => {
    return useMutation({
      mutation: (targets: string[]): Promise<PrDetailsResponse> => service.getPrDetails(targets),
      onSuccess: ({ prs }) => {
        const work = queryCache.getQueryData<MyWork>(CACHE_KEYS.myWork.overview())
        if (!work || !prs.length) return
        const updated = withPrDetails(work, prs)
        rememberMyWork(updated)
        queryCache.setQueryData(CACHE_KEYS.myWork.overview(), updated)
      }
    })
  }

  /** Saves the person's merge settings: the automatic and clean-verdict repo lists, and open non-blocking findings. */
  const useSaveMergeSettings = () => {
    return useMutation({
      mutation: (settings: SaveMergeSettingsRequest): Promise<MergeSettings> =>
        service.saveMergeSettings(settings),
      onSuccess: invalidateMyWork
    })
  }

  /** What the dialog's unsaved settings would do to the person's open PRs: a read, so nothing is invalidated. */
  const usePreviewMergeSettings = () => {
    return useMutation({
      mutation: (settings: SaveMergeSettingsRequest): Promise<MergePreviewResponse> =>
        service.previewMergeSettings(settings)
    })
  }

  return {
    useMyWork,
    useMyMachineThanks,
    useRefreshMyWork,
    useStartAction,
    useAnswerDecision,
    useStartSweep,
    useMyWorkCounts,
    useMergePrs,
    useSetAutoMerge,
    useRefreshPr,
    usePrDetails,
    useSaveMergeSettings,
    usePreviewMergeSettings
  }
}
